import {
  catalogBySlug,
  classifyWorkout,
  formatShortDayLabel,
  parseLocaleNumber,
  type EffortScale,
  type Exercise,
  type NamingEntry,
} from '@heraion/shared';
import type { LoggerDraft } from './state/loggerDraftTypes';
import { savedRirOf } from './state/canonicalRir';

// Auto-suggested workout name (#367, Antenore 2026-10-17): "<what was trained>
// - <short date>", e.g. "Full body - Tue 29", "Arms & Core - Sun 15". What was
// trained is classifyWorkout (shared): the loaded sets, by region. It's a LIVE
// suggestion: it follows the completed sets, and is never persisted as a
// snapshot, it's the fallback the save path applies only when the user left
// the name blank ([[feedback_no_denormalized_display_snapshots]]).
// Replaces "<Weekday> W<isoWeek> · <Region>+<Region>" (2026-06-23); a name
// already saved keeps whatever it was saved with.

export type NameSuggestInput = {
  draft: LoggerDraft;
  catalog: ReadonlyArray<Exercise> | null;
  effortScale: EffortScale;
  locale: string;
  // i18n `t`: mobile.logger.autoName.* and catalog.region_*.
  t: (key: string, opts?: Record<string, string>) => string;
};

// The draft as the namer reads it: COMPLETED sets only (the save drops the
// rest). A set's effort is what the save would store: savedRirOf, the same
// reader buildPayload uses, plus fail (fail = RIR 0, in effectiveRir). An
// AMRAP carries its result in that same rir/rpe cell, nothing special here.
function namingEntries(
  draft: LoggerDraft,
  catalog: ReadonlyArray<Exercise> | null,
  effortScale: EffortScale,
): NamingEntry[] {
  const bySlug = catalogBySlug(catalog ?? []);
  return draft.entries.map((e) => ({
    row: bySlug.get(e.exerciseId) ?? null,
    isCardio: e.isCardio,
    sets: e.sets
      .filter((s) => s.completedAt !== null)
      .map((s) => {
        const w = parseLocaleNumber(s.weight);
        return {
          fail: s.fail,
          rir: savedRirOf(s.rir, s.rpe, effortScale),
          hasExternalLoad: !e.isCardio && Number.isFinite(w) && w > 0,
        };
      }),
  }));
}

export function suggestWorkoutName(input: NameSuggestInput): string {
  const date = formatShortDayLabel(input.draft.startedAt, input.locale);
  if (!date) return '';
  const { t } = input;
  const kind = classifyWorkout(
    namingEntries(input.draft, input.catalog, input.effortScale),
  );
  if (!kind) return date;
  const label =
    kind.kind === 'regions'
      ? kind.regions
          .map((r) => t(r === 'lower' ? 'mobile.logger.autoName.region_lower' : `catalog.region_${r}`))
          .reduce((a, b) => t('mobile.logger.autoName.pair', { a, b }))
      : t(`mobile.logger.autoName.${kind.kind}`);
  return t('mobile.logger.autoName.title', { label, date });
}
